import { getObjectEntries } from '@myre/common-primitives';

import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { isObject, uniqBy } from 'lodash-es';

import { MyuiLevel } from '../abstract/abstract.attribute';
import { MyuiComponent } from '../abstract/abstract.component';
import { MyuiTemplate } from '../abstract/abstract.template';
import { MyuiHint } from '../hint/hint.component';
import { type MyuiHintContext } from '../hint/hint.interface';

import { type MyuiHintListOption } from './hint-list.interface';

@Component({
  selector: 'myui-hint-list',
  templateUrl: 'hint-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MyuiHint, MyuiTemplate, NgClass, NgStyle, NgTemplateOutlet],
})
export class MyuiHintList extends MyuiComponent {
  public override mSelector = 'myui-hint-list';

  public readonly hints = input<MyuiHintListOption[] | ValidationErrors | null>(
    null,
  );

  public readonly hasHints = computed(() => !!this.orderedHints().length);

  public readonly orderedHints = computed<MyuiHintListOption[]>(() => {
    let result: MyuiHintListOption[] = [];
    const hints = this.hints();

    if (isObject(hints)) {
      if (this._areHintOptions(hints)) {
        result = hints.sort((a, b) => {
          return (b.order ?? 0) - (a.order ?? 0);
        });
      } else {
        result = uniqBy(
          getObjectEntries(hints ?? {}).filter(([key, value]) => key && value),
          ([k]) => k,
        ).map(([key], index) => {
          return {
            text: key,
            order: index,
            level: MyuiLevel.Secondary,
          } as MyuiHintListOption;
        });
      }
    }

    return result;
  });

  public readonly itemTemplate = computed(() =>
    this._getTemplate<MyuiHintContext>('item'),
  );

  protected _getItemContext(hint: MyuiHintListOption): MyuiHintContext {
    return {
      level: hint.level,
      $implicit: hint.text,
    };
  }

  private _areHintOptions(value: unknown): value is MyuiHintListOption[] {
    return Array.isArray(value) && value.every(v => isObject(v));
  }
}
